import {
  createTRPCClient,
  httpBatchLink,
  type TRPCClient,
  TRPCClientError,
  type TRPCLink,
} from '@trpc/client';
import { observable } from '@trpc/server/observable';

import { infinexErrorResponseSchema } from '@infinex-private/connect-types';
import { InfinexError } from '@infinex-private/connect-utils';
import { type ConnectWorkerRouter } from '@infinex/connect-service-contract/client';

export type { ConnectWorkerRouter, TRPCClient };

export const createTrpcClient = (
  trpcUrl: string,
  globalHeaders: Headers,
  authorizationToken?: () => string | undefined,
  idToken?: () => string | undefined
) => {
  return createTRPCClient<ConnectWorkerRouter>({
    links: [
      infinexErrorLink,
      httpBatchLink({
        url: trpcUrl,
        fetch(url, options) {
          const headers = new Headers(options?.headers ?? {});
          globalHeaders.forEach((value, key) => headers.set(key, value));

          const authorizationHeaderValue = authorizationToken?.();
          if (authorizationHeaderValue) {
            headers.set('Authorization', `Bearer ${authorizationHeaderValue}`);
          }

          const idTokenHeaderValue = idToken?.();
          if (idTokenHeaderValue) {
            headers.set('Authorization', `IdToken ${idTokenHeaderValue}`);
          }

          return fetch(url, {
            ...options,
            headers,
            credentials: 'include',
          });
        },
      }),
    ],
  });
};

const infinexErrorLink: TRPCLink<ConnectWorkerRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          const cause = err.data && 'cause' in err.data ? err.data.cause : null;
          const parsedCause = infinexErrorResponseSchema.safeParse(cause);
          if (!parsedCause.success) {
            return observer.error(err);
          }
          observer.error(
            new TRPCClientError('Infinex error', {
              cause: new InfinexError(parsedCause.data),
            })
          );
        },
        complete() {
          observer.complete();
        },
      });
      return unsubscribe;
    });
  };
};
